import { useRef, useState } from "react";

import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import { FieldError, FieldValues, useForm } from "react-hook-form";
import { toast } from "react-toastify";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ErrorForm } from "@/common/components/form/ErrorForm";
import Input from "@/common/components/form/Input";
import Loader from "@/common/components/Loader";
import { phoneRule } from "@/common/formValidation";

import User from "../../../assets/img/user.png";
import edit from "../../../assets/svg/edit.svg";
import { IUser } from "../interfaces/user";
import { getAllPersonTitles, updateUser } from "../services";
import { UserType } from "./ProfileComponent";
import { IPersonTitle } from "../interfaces/person-title";


interface IPersonTitleData {
  personTitles: IPersonTitle[];
}

const YourInfos = (props: {
  userInfos: UserType;
  handleChangeUserInfos: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleChangeImage: (image: File) => void;
}) => {
  const queryClient = useQueryClient();
  const [disabledControls, setDisabledControls] = useState(true);
  const [uploadedImage, setUploadedImage] = useState(undefined);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const {
    data: personTitleData,
    isLoading: personTitleLoading,
    error: personTitleError,
  } = useQuery<IPersonTitle[], Error>([], () => getAllPersonTitles(), {
    // enabled: !!id,
    staleTime: Infinity,
  });

  const {
    control,
    formState: { errors },
    register,
    watch,
    handleSubmit,
  } = useForm<FieldValues>({
    defaultValues: {
      titleId: props.userInfos.titleId ?? "",
      firstname: props.userInfos.firstname ?? "",
      lastname: props.userInfos.lastname ?? "",
      email: props.userInfos.email ?? "",
      phoneNumber: props.userInfos.phoneNumber ?? "",
    },
  });

  const selectedTitleId = watch("titleId");

  const onSubmit = async (data: FieldValues) => {
    mutationUser.mutate(data as IUser);
  };

  const mutationUser = useMutation({
    mutationFn: (userData: IUser) => updateUser(props.userInfos.id, userData),
    onError: (error) => {
      console.error(error);
      toast.error("Erreur lors de la sauvegarde de l'utilisateur");
    },
    onSuccess: async () => {
      setDisabledControls(true);
      toast.success("Utilisateur mis à jour avec succès");
      queryClient.invalidateQueries({ queryKey: ["auth", props.userInfos.id] });
    },
  });

  const handleDisableInputs = () => {
    setDisabledControls(false);
    setTimeout(() => {
      if (null !== inputRef && null !== inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
  };

  const changeUploadedImage = (event: any) => {
    setUploadedImage(event.target.files[0]);
  };

  const handleSaveImage = (_e: any) => {
    uploadedImage && props.handleChangeImage(uploadedImage);
    setUploadedImage(undefined);
  };

  const renderPersonTitles = () => {
    if (personTitleData) {
      const titleData: unknown = personTitleData;
      const { personTitles } = titleData as IPersonTitleData;
      return personTitles.map((personTitle: IPersonTitle) => {
        const { id, title } = personTitle;
        return (
          <option key={id} value={id}>
            {title}
          </option>
        );
      });
    }

    return <></>;
  };
  if (personTitleLoading) {
    return <Loader />;
  }
  if (personTitleError) {
    return (
      <div className="bg-container">
        <Alert variant="danger">
          Erreur chargement des données : {personTitleError}
        </Alert>
      </div>
    );
  }
  if (!personTitleData) {
    return (
      <div className="bg-container">
        <Alert variant="danger">
          Erreur de récupération des données :<br />
          Veuillez réessayer ultérieurement.
        </Alert>
      </div>
    );
  }

  return (
    <>
      <Row className="accountData">
        {/* Photo */}
        <Col lg={4} xs={12} className="mb-4">
          <div className="bg-container h-100">
            <h2 className="mt-1 fw-bold" style={{ fontSize: "24px" }}>
              Votre photo
            </h2>
            <div className="d-flex flex-column align-items-center mt-5">
              {uploadedImage !== undefined && (
                <img
                  src={URL.createObjectURL(uploadedImage)}
                  alt="user"
                  style={{
                    borderRadius: "50%",
                    width: "140px",
                    height: "140px",
                  }}
                />
              )}
              {props.userInfos.image && uploadedImage === undefined && (
                <img
                  src={URL.createObjectURL(props.userInfos.image)}
                  alt="user"
                  style={{
                    borderRadius: "50%",
                    width: "140px",
                    height: "140px",
                  }}
                />
              )}
              {!props.userInfos.image && uploadedImage === undefined && (
                <img
                  src={User}
                  alt="user"
                  style={{
                    borderRadius: "50%",
                    width: "140px",
                    height: "140px",
                  }}
                />
              )}
              <label className="btn-change mt-3 cursor-pointer">
                Changer
                <Form.Control
                  id="file"
                  type="file"
                  name="image"
                  className="d-none"
                  onChange={changeUploadedImage}
                  accept="image/png, image/jpeg"
                />
              </label>
              {uploadedImage !== undefined && (
                <Button
                  variant="primary"
                  className="mt-4"
                  onClick={handleSaveImage}
                >
                  Enregistrer
                </Button>
              )}
            </div>
          </div>
        </Col>
        <Col lg={8} xs={12} className="mb-4">
          <form onSubmit={handleSubmit(onSubmit)} className="bg-container">
            <Row>
              <Col xs={8}>
                <h2 className="mt-1 fw-bold" style={{ fontSize: "24px" }}>
                  Vos coordonnées
                </h2>
              </Col>
              <Col
                xs={4}
                className="d-flex justify-content-end"
                style={{ height: "42px" }}
              >
                {disabledControls ? (
                  <button
                    className="btn btn-actions btn-edit-order cursor-pointer"
                    onClick={handleDisableInputs}
                  >
                    <img
                      src={edit}
                      alt="edit"
                      style={{ width: "12px", height: "12px" }}
                    />
                  </button>
                ) : (
                  <Button type="submit" variant="primary">
                    Enregistrer
                  </Button>
                )}
              </Col>
            </Row>

            <Row className="row mt-3">
              <Col md={6} sm={12}>
                <div className="form-input_label small-font-size mb-1">
                  Titre <span className="text-danger">*</span>
                </div>
                <select
                  {...register("titleId", {
                    required: "Veuillez sélectionner votre titre",
                  })}
                  name="titleId"
                  disabled={disabledControls}
                  className={`form-select profile-title-select small-font-size bg-body-tertiary ${!selectedTitleId ? "text-body-tertiary" : ""}`}
                >
                  <option value="">Sélectionner ...</option>
                  {renderPersonTitles()}
                </select>
                <ErrorForm error={errors.titleId as FieldError} />
              </Col>
            </Row>
            <Row className="row mt-3">
              <Col md={6} sm={12}>
                <Input
                  control={control}
                  label="Nom"
                  name={"lastname"}
                  defaultValue=""
                  rules={{ required: "Veuillez renseigner un nom" }}
                  disabled={disabledControls}
                />
              </Col>
              <Col md={6} sm={12}>
                <Input
                  control={control}
                  label="Prénom"
                  name={"firstname"}
                  defaultValue=""
                  rules={{ required: "Veuillez renseigner un prénom" }}
                  disabled={disabledControls}
                />
              </Col>
            </Row>
            <Row className="mt-3">
              <Col md={6} sm={12}>
                <Input
                  control={control}
                  label="Téléphone"
                  name={"phoneNumber"}
                  defaultValue=""
                  rules={{
                    required: "Veuillez renseigner un numéro de téléphone",
                    ...phoneRule,
                  }}
                  disabled={disabledControls}
                />
              </Col>
              <Col md={6} sm={12}>
                <Input
                  control={control}
                  label="Email"
                  name={"email"}
                  defaultValue=""
                  rules={{ required: "Veuillez renseigner un email" }}
                  disabled={disabledControls}
                />
              </Col>
            </Row>
          </form>
        </Col>
      </Row>
    </>
  );
};

export default YourInfos;
